import { ChevronDown, Eye, EyeOff, Play } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type StartActionsProps = {
  /** Draft or QA: the header shows End or Ended for everything else. */
  inQa: boolean;
  /** False for a Server Side experiment, whose variants the customer's own code shows. */
  canQa: boolean;
  /** Why nothing can start (missing URL rule, destination, variants still loading), or undefined. */
  blockedReason: string | undefined;
  /** Why Start experiment alone cannot run (no edit permission on the GA property), or undefined. */
  startBlockedReason: string | undefined;
  isStarting: boolean;
  qaPending: boolean;
  onStart: () => void;
  onStartQa: () => void;
  onStopQa: () => void;
};

const ITEM_CLASS = 'text-foreground focus:bg-hover rounded-md text-meta flex items-start gap-2 py-2';

type TriggerLabelParams = { inQa: boolean; isStarting: boolean; qaPending: boolean };

function triggerLabel({ inQa, isStarting, qaPending }: TriggerLabelParams): string {
  if (isStarting) return 'Starting...';
  if (qaPending) return inQa ? 'Stopping QA...' : 'Starting QA...';
  return inQa ? 'QA on' : 'Start';
}

/**
 * The draft's actions, in one menu: Start QA (preview it on the live site, never shown to visitors
 * and tracked by nothing, for three days) or Start experiment. In QA it offers Stop QA in place
 * of Start QA, and starting goes straight from QA. Switching QA off and on has no limit. A draft
 * QA cannot preview (Server Side) keeps the plain Start button: a menu of one item is a detour.
 */
export const StartActions = ({
  inQa,
  canQa,
  blockedReason,
  startBlockedReason,
  isStarting,
  qaPending,
  onStart,
  onStartQa,
  onStopQa,
}: StartActionsProps) => {
  const busy = isStarting || qaPending;
  const label = triggerLabel({ inQa, isStarting, qaPending });

  if (!canQa && !inQa) {
    const reason = blockedReason ?? startBlockedReason;
    return (
      <Button
        title={reason}
        disabled={isStarting || !!reason}
        onClick={onStart}
        data-tour="experiment-start"
        className="bg-cta hover:bg-ctahover h-8 min-w-[132px] rounded-lg px-3 text-meta font-medium text-white"
      >
        {isStarting ? 'Starting...' : 'Start Experiment'}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          // Never blocked while in QA: Stop QA must stay reachable whatever stops a start.
          title={inQa ? undefined : blockedReason}
          disabled={busy || (!inQa && !!blockedReason)}
          // Rendered in both the desktop and the mobile row; the tour finds whichever is shown.
          data-tour="experiment-start"
          className={cn(
            'h-8 min-w-[132px] gap-1.5 rounded-lg px-3 text-meta font-medium',
            inQa
              ? 'border-line text-foreground hover:bg-hover border bg-transparent'
              : 'bg-cta hover:bg-ctahover text-white',
          )}
        >
          {inQa && <Eye className="size-3.5" strokeWidth={1.5} />}
          {label}
          <ChevronDown className="size-3.5" strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="border-line bg-panel w-72 rounded-lg">
        {inQa ? (
          <DropdownMenuItem onClick={onStopQa} className={ITEM_CLASS}>
            <EyeOff className="mt-0.5 size-3.5 shrink-0" strokeWidth={1.5} />
            <span className="flex flex-col">
              <span className="font-medium">Stop QA</span>
              <span className="text-dim text-micro">Back to draft. The preview links stop working.</span>
            </span>
          </DropdownMenuItem>
        ) : (
          canQa && (
            <DropdownMenuItem onClick={onStartQa} disabled={!!blockedReason} className={ITEM_CLASS}>
              <Eye className="mt-0.5 size-3.5 shrink-0" strokeWidth={1.5} />
              <span className="flex flex-col">
                <span className="font-medium">Start QA</span>
                <span className="text-dim text-micro">
                  Preview it on the live site with a link. Visitors never see it, nothing is tracked, and it ends after
                  3 days.
                </span>
              </span>
            </DropdownMenuItem>
          )
        )}
        <DropdownMenuItem onClick={onStart} disabled={!!(blockedReason ?? startBlockedReason)} className={ITEM_CLASS}>
          <Play className="mt-0.5 size-3.5 shrink-0" strokeWidth={1.5} />
          <span className="flex flex-col">
            <span className="font-medium">Start experiment</span>
            <span className="text-dim text-micro">
              {blockedReason ?? startBlockedReason ?? 'Show it to visitors and start measuring.'}
            </span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
